import { useEffect, useRef, useState } from 'react';
import { m } from 'framer-motion';
import { timing } from '../motion.js';
import { Roll } from './primitives.js';

const clamp = (n) => Math.max(0, Math.min(99, n));

/**
 * The only score control. Mouse click, Space and ↑/→ add a run; ↓/←/Backspace remove one.
 * A touch tap opens the number pad. Typing a digit or pressing Enter starts typing.
 * The input stays mounted and is focused inside pointerup, or iOS won't open the keyboard.
 */
export function Score({ value, onChange, label, leading }) {
  const input = useRef(null);
  const [typing, setTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const [changing, setChanging] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    setChanging(true);
    const t = setTimeout(() => setChanging(false), timing.scoreLightMs);
    return () => clearTimeout(t);
  }, [value]);

  const lit = changing || typing;
  const fade = { duration: lit ? 0.3 : 0.7, ease: 'easeOut' };

  const startTyping = (seed) => {
    const start = seed ?? String(value);
    setDraft(start);
    setTyping(true);
    const el = input.current;
    if (!el) return;
    el.value = start;
    el.focus({ preventScroll: true });
    if (seed == null) el.select();
    else el.setSelectionRange(start.length, start.length);
  };

  const cancelled = useRef(false);
  const commit = () => {
    const n = parseInt(draft, 10);
    if (!cancelled.current && !Number.isNaN(n)) onChange(clamp(n));
    cancelled.current = false;
    setTyping(false);
  };

  return (
    <div className={`st-score${leading ? ' st-score--lead' : ''}${typing ? ' st-score--typing' : ''}`}>
      <div className="st-dwrap">
        <m.i className="st-light" aria-hidden="true" initial={false} animate={{ opacity: lit ? 1 : 0 }} transition={fade} />
        <m.div
          className="st-digits"
          role="spinbutton"
          tabIndex={typing ? -1 : 0}
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={99}
          aria-label={`${label} runs. Click or press Space to add a run, type a number to set it`}
          whileTap={typing ? undefined : { scale: 0.95 }}
          onPointerUp={(e) => {
            if (typing) return;
            if (e.pointerType === 'mouse') {
              onChange((v) => clamp(v + 1));
              return;
            }
            e.preventDefault();
            startTyping();
          }}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (typing) return;
            if (/^[0-9]$/.test(e.key)) {
              e.preventDefault();
              startTyping(e.key);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              startTyping();
            } else if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'ArrowRight') {
              e.preventDefault();
              onChange((v) => clamp(v + 1));
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'Backspace') {
              e.preventDefault();
              onChange((v) => clamp(v - 1));
            }
          }}
        >
          <m.i className="st-dglow" aria-hidden="true" initial={false} animate={{ opacity: lit ? 1 : 0 }} transition={fade} />
          <span className="st-dval" style={{ opacity: typing ? 0 : 1 }}>
            <Roll value={value} />
          </span>
          <input
            ref={input}
            className="st-digits-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            tabIndex={-1}
            aria-label={`Type ${label} runs`}
            value={typing ? draft : String(value)}
            style={{ opacity: typing ? 1 : 0, pointerEvents: typing ? 'auto' : 'none' }}
            onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelled.current = true;
                e.currentTarget.blur();
              }
            }}
          />
        </m.div>
      </div>
    </div>
  );
}
